import { createTheme } from "@mui/material/styles";

import {
  fieldErrorColor,
  fieldErrorFontSize,
  kickBlue,
  kickLightBlue,
} from "./baseStyles.js";
import "./fonts.css";

// Shared MUI v5 theme with MUI v4-compatible defaults.
// All three bundles (visits, emr, patient-portal) use this theme.
const theme = createTheme({
  typography: {
    fontFamily: '"BasisGrotesquePro-Regular", "Helvetica", "Arial", sans-serif',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: "#3f51b5", // MUI v4 default primary (indigo)
    },
    secondary: {
      main: "#f50057", // MUI v4 default secondary (pink)
    },
    error: {
      // MUI v4 default error red; also the field-error colour (baseStyles.js).
      main: fieldErrorColor,
    },
    warning: {
      main: "#ff9800", // MUI v4 default warning (orange)
    },
    info: {
      main: "#2196f3", // MUI v4 default info (blue)
    },
    success: {
      main: "#4caf50", // MUI v4 default success (green)
    },
  },
  components: {
    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiButton: {
      styleOverrides: {
        containedInherit: {
          "&:hover": {
            backgroundColor: "#999",
            color: "#fff",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "18px",
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            fontSize: "24px",
          },
        },
      },
    },
  },
});

export default theme;

// patientTheme is the theme of the patient-facing bundles (visits,
// patient-portal): the shared theme plus the site's field-error look, defined
// here once so every MUI TextField / FormHelperText picks it up. The outline
// is MUI's own error state in the theme's error red (1px at rest, like any
// outline); the message under the field is 14px rather than MUI's 12px
// caption, so it reads as a sentence on a phone. The EMR keeps `theme`.
// A step darker than kickLightBlue (#f4f2f9), which is too pale to read as
// "this one" against the white list.
export const AUTOCOMPLETE_HIGHLIGHT_BACKGROUND = "#e9e7f5";

export const patientTheme = createTheme(theme, {
  components: {
    // Autocomplete lists (the signup and checkout state pickers): the
    // highlighted option is what Enter / Tab / blur will select, so it gets a
    // lavender tint, a Kick-blue bar and medium weight instead of MUI's 4% grey.
    // MUI moves the highlight to the option under the pointer, so hover looks
    // the same. Every option carries a transparent bar so text doesn't shift.
    //
    // On `listbox`, not `option`: MUI applies option overrides on the popper,
    // and its own option colours (set on the listbox, which renders later) beat
    // them. It also resets the highlight to transparent on touch screens
    // (`@media (hover: none)`); these rules come after that reset at equal or
    // higher specificity, so phones see the highlight too, which matters
    // because tapping away selects it.
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          "& .MuiAutocomplete-option": {
            borderLeft: "3px solid transparent",
            '&[aria-selected="true"]': {
              backgroundColor: kickLightBlue,
            },
            '&.Mui-focused, &.Mui-focusVisible, &[aria-selected="true"].Mui-focused, &[aria-selected="true"].Mui-focusVisible':
              {
                backgroundColor: AUTOCOMPLETE_HIGHLIGHT_BACKGROUND,
                borderLeftColor: kickBlue,
                fontWeight: 500,
              },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            fontSize: fieldErrorFontSize,
            lineHeight: 1.4,
          },
        },
      },
    },
  },
});
